// Saved filter presets for the Incidents page.
// 3 system presets (non-deletable) + up to 5 user presets per user, stored in
// localStorage under sitroom_filter_presets_{user_id}.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, X, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { activeFilterCount } from "./IncidentFilters";

const MAX_USER_PRESETS = 5;
const PRESET_FIELDS = [
  "status", "category", "tenant_id", "plant", "opened_by",
  "date_from", "date_to", "sla_status", "min_eur_loss",
  "has_8d", "has_lpa", "q", "sev",
];

const todayISO = () => new Date().toISOString().slice(0, 10);

// 3 system presets — non-deletable, always shown first
const SYSTEM_PRESETS = [
  {
    id: "sys-s1-active",
    name: "S1 Active",
    icon: "🔴",
    system: true,
    filters: {
      sev: "s1",
      status: "open,acknowledged,in_progress,escalated",
    },
  },
  {
    id: "sys-sla-breach",
    name: "SLA Breach",
    icon: "🚨",
    system: true,
    filters: { sla_status: "breach" },
  },
  {
    id: "sys-escalated-today",
    name: "Escalated Today",
    icon: "⬆",
    system: true,
    filters: { status: "escalated", date_from: null /* set dynamically */ },
  },
];

const storageKey = (uid) => `sitroom_filter_presets_${uid || "anon"}`;

const loadUserPresets = (uid) => {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_USER_PRESETS) : [];
  } catch {
    return [];
  }
};

const saveUserPresets = (uid, presets) => {
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(presets.slice(0, MAX_USER_PRESETS)));
  } catch {
    /* ignore */
  }
};

// Extract current filter values from URL params (only the keys we know about)
const snapshotFilters = (params) => {
  const out = {};
  for (const k of PRESET_FIELDS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
};

const applyPreset = (params, preset, setParams) => {
  const next = new URLSearchParams(params);
  // Clear all known filter keys first
  for (const k of PRESET_FIELDS) next.delete(k);
  // Then apply preset values
  const filters = { ...preset.filters };
  // Dynamic "today" rewrite for the system Escalated Today preset
  if (preset.id === "sys-escalated-today") filters.date_from = todayISO();
  for (const [k, v] of Object.entries(filters)) {
    if (v === null || v === undefined || v === "") continue;
    next.set(k, String(v));
  }
  setParams(next, { replace: true });
};

export const IncidentFilterPresets = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [userPresets, setUserPresets] = useState([]);

  useEffect(() => {
    setUserPresets(loadUserPresets(user?.id));
  }, [user?.id]);

  const activeCount = activeFilterCount(params);
  const canSave = activeCount > 0 && userPresets.length < MAX_USER_PRESETS;

  // Detect which preset (if any) currently matches the URL exactly
  const currentSnapshot = useMemo(() => snapshotFilters(params), [params]);
  const matchesPreset = (preset) => {
    const f = { ...preset.filters };
    if (preset.id === "sys-escalated-today") f.date_from = todayISO();
    // Compare cleaned values
    const keys = new Set([...Object.keys(f), ...Object.keys(currentSnapshot)]);
    for (const k of keys) {
      const a = (currentSnapshot[k] || "").toString();
      const b = (f[k] === null || f[k] === undefined ? "" : f[k]).toString();
      if (a !== b) return false;
    }
    return true;
  };

  const handleSavePreset = () => {
    const name = presetName.trim().slice(0, 30);
    if (!name) return;
    const filters = snapshotFilters(params);
    if (Object.keys(filters).length === 0) return;
    const newPreset = {
      id: `usr-${Date.now()}`,
      name,
      icon: "⭐",
      system: false,
      filters,
    };
    const next = [...userPresets, newPreset].slice(0, MAX_USER_PRESETS);
    setUserPresets(next);
    saveUserPresets(user?.id, next);
    setPresetName("");
    setShowSaveModal(false);
  };

  const handleDeletePreset = (id) => {
    const next = userPresets.filter((p) => p.id !== id);
    setUserPresets(next);
    saveUserPresets(user?.id, next);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="filter-presets">
        {[...SYSTEM_PRESETS, ...userPresets].map((preset) => {
          const active = matchesPreset(preset);
          return (
            <span
              key={preset.id}
              className="inline-flex items-center gap-1.5 rounded-full"
              style={{
                background: active ? "rgba(155,89,182,0.20)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${active ? "rgba(155,89,182,0.55)" : "rgba(255,255,255,0.10)"}`,
                color: active ? "#c89adb" : "#c5cad8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.4px",
                padding: "4px 4px 4px 10px",
              }}
              data-testid={`preset-chip-${preset.id}`}
            >
              <button
                onClick={() => applyPreset(params, preset, setParams)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
                data-testid={`preset-apply-${preset.id}`}
              >
                <span>{preset.icon}</span>
                <span>{preset.name}</span>
              </button>
              {!preset.system && (
                <button
                  onClick={() => handleDeletePreset(preset.id)}
                  className="rounded-full ml-1 flex items-center justify-center"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#9198b0",
                    cursor: "pointer",
                    padding: 0,
                    width: 16,
                    height: 16,
                  }}
                  aria-label={`Delete preset ${preset.name}`}
                  data-testid={`preset-delete-${preset.id}`}
                >
                  <X size={10} />
                </button>
              )}
              {preset.system && <span style={{ width: 6 }} />}
            </span>
          );
        })}

        {/* Save current filters as preset */}
        {activeCount > 0 && (
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-full px-3"
            style={{
              background: canSave ? "rgba(78,208,132,0.10)" : "rgba(255,255,255,0.03)",
              border: `1px dashed ${canSave ? "rgba(78,208,132,0.55)" : "rgba(255,255,255,0.10)"}`,
              color: canSave ? "#4ed084" : "#5a6080",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.4px",
              padding: "4px 10px",
              cursor: canSave ? "pointer" : "not-allowed",
              height: 24,
            }}
            data-testid="save-filter-btn"
            title={canSave ? "Save current filters as a preset" : `Max ${MAX_USER_PRESETS} presets reached`}
          >
            <Star size={10} />
            Save filter
          </button>
        )}
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.7)", zIndex: 800 }}
          onClick={() => setShowSaveModal(false)}
          data-testid="save-preset-modal"
        >
          <div
            className="rounded-lg p-5"
            style={{
              width: "min(380px, 92vw)",
              background: "#0a0c12",
              border: "1px solid rgba(155,89,182,0.40)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center gap-2 mb-3"
              style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 14, color: "#f0f2f8" }}
            >
              <Star size={14} style={{ color: "#c89adb" }} />
              Save filter preset
            </div>
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value.slice(0, 30))}
              maxLength={30}
              placeholder="e.g. S1 Breach BMW"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSavePreset();
                if (e.key === "Escape") setShowSaveModal(false);
              }}
              className="w-full rounded px-3 py-2 mb-3"
              style={{
                background: "#0d0f18",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#f0f2f8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                outline: "none",
              }}
              data-testid="save-preset-input"
            />
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "#9198b0",
                marginBottom: 12,
                letterSpacing: "0.4px",
              }}
            >
              {presetName.length}/30 · {userPresets.length}/{MAX_USER_PRESETS} presets used
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-3 py-1.5 rounded"
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "#9198b0",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: "0.4px",
                  cursor: "pointer",
                }}
                data-testid="save-preset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded"
                style={{
                  background: presetName.trim() ? "#9b59b6" : "rgba(155,89,182,0.20)",
                  border: "1px solid rgba(155,89,182,0.55)",
                  color: presetName.trim() ? "#fff" : "#9198b0",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: "0.4px",
                  cursor: presetName.trim() ? "pointer" : "not-allowed",
                }}
                data-testid="save-preset-save"
              >
                <Save size={11} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
